/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import classNames from "classnames";
import { ChatState } from "PFApp/components/chat/chat_states";
import InviteModal from "PFApp/conversations/invite/invite_modal";
import { useForceUpdate } from "PFCore/helpers/use_force_update";
import { Conversation, CurrentProfile } from "PFTypes";
import { ChatParticipant } from "PFTypes/chat_participant";
import { CSSProperties, useCallback, useState } from "react";

import css from "./chat.module.scss";
import { ChatBodyWrap } from "./chat_body_wrap";
import { ChatHeader } from "./chat_header";
import { ChatStatePanel } from "./chat_state_panel";
import { ChatMessagesData } from "./chat_types";
import { getChatHeaderInfo, getOtherPerson } from "./get_chat_header_info";
import { setChatSendOnEnter } from "./send_on_enter";
import { useChatAttachments } from "./use_chat_attachments";
import { useChatCurrentParticipant } from "./use_chat_current_participant";
import { useChatDropdown } from "./use_chat_dropdown";
import { useChatWindowHandlers } from "./use_chat_window_handlers";

type ChatProps = {
  chatState: ChatState;
  messages?: ChatMessagesData;
  conversation: Conversation;
  participants: ChatParticipant[];
  selected: boolean;
  setChatState: (chatState: ChatState) => void;
  emptyMessage: string;
  currentProfile: CurrentProfile;
  flashTitle?: boolean;
  isFetchingPreviousMessages?: boolean;

  style?: CSSProperties;
  isBelow?: boolean;
  minimized: boolean;
  expanded: boolean;
  expandable: boolean;

  onSelect: (id: number) => void;
  onClose?: (id: number) => void;
  onMinimize?: (id: number) => void;
  onExpand?: (id: number, expand: boolean) => void;
  onUnMinimize?: (id: number) => void;
  onLoadPreviousMessages?: () => void;
};

// fallow-ignore-next-line complexity -- wires sub-hooks/handlers; further split fragments the render tree
export const Chat = ({
  chatState,
  conversation,
  currentProfile,
  emptyMessage,
  expandable = true,
  expanded,
  onClose,
  onExpand,
  onMinimize,
  onSelect,
  onUnMinimize,
  onLoadPreviousMessages,
  isBelow,
  messages,
  minimized,
  participants,
  selected,
  setChatState,
  style,
  flashTitle,
  isFetchingPreviousMessages
}: ChatProps) => {
  const forceUpdate = useForceUpdate();
  const [inviteModalShown, setInviteModalShown] = useState(false);

  const { dropdownOpen, setDropdownOpen, dropdownFocusRef } = useChatDropdown(chatState, messages, minimized);
  const { currentParticipant, handleFollowingChange } = useChatCurrentParticipant(
    participants,
    conversation,
    currentProfile
  );
  const {
    attachments,
    handleAddAttachment,
    handleRemoveAttachment,
    handleClearAttachments,
    handleAttachmentSaveFailed
  } = useChatAttachments();

  const { handleUnMinimize, handleExpand, handleMinimize, handleClose } = useChatWindowHandlers({
    conversationId: conversation.id,
    expanded,
    onSelect,
    onClose,
    onMinimize,
    onExpand,
    onUnMinimize,
    setDropdownOpen
  });

  const showParticipants = useCallback(() => {
    setChatState(ChatState.Participants);
  }, [setChatState]);

  const isPrivate = conversation.kind === "normal";
  const unreadCount = conversation.unreadCount ?? 0;
  const otherPerson = getOtherPerson(participants, currentProfile);

  const { title, isFollowing, targetUrl } = getChatHeaderInfo({
    chatState,
    conversation,
    currentParticipant,
    otherPerson
  });

  const handleSendOnEnter = (val: boolean) => {
    setChatSendOnEnter(val);
    forceUpdate();
  };

  const handleRootClick = () => onSelect(conversation.id);
  const handleShowInviteModal = () => setInviteModalShown(true);
  const handleCloseInviteModal = () => setInviteModalShown(false);
  const handleCloseParticipants = () => setChatState(ChatState.Normal);
  const handleChatMenuChange = (fn?: () => void) => {
    setDropdownOpen(false);
    fn?.();
  };

  return (
    <div
      className={classNames(css.root, {
        [css.minimized]: minimized,
        [css.selected]: selected,
        [css.expanded]: expanded
      })}
      style={style}
      onClick={handleRootClick}
      data-qa-id="chat-root"
    >
      {inviteModalShown && (
        <InviteModal
          conversation={conversation}
          participants={participants}
          handleClose={handleCloseInviteModal}
        />
      )}
      <ChatHeader
        conversation={conversation}
        info={{ title, targetUrl, otherPerson, isPrivate, flashTitle, unreadCount }}
        windowState={{ selected, minimized, isBelow, chatState, expandable, expanded, dropdownOpen }}
        handlers={{
          onHeaderClick: handleUnMinimize,
          onMinimize: handleMinimize,
          onExpand: handleExpand,
          onClose: handleClose,
          onToggleDropdown: setDropdownOpen
        }}
        dropdownFocusRef={dropdownFocusRef}
        chatMenuHandlers={{
          showParticipants,
          handleChange: handleChatMenuChange,
          setSendOnEnter: setChatSendOnEnter,
          isFollowing,
          handleFollowingChange,
          handleSendOnEnter,
          handleInviteToChat: handleShowInviteModal
        }}
      />
      <ChatBodyWrap minimized={minimized}>
        <ChatStatePanel
          chatState={chatState}
          conversation={conversation}
          currentProfile={currentProfile}
          isPrivate={isPrivate}
          isFollowing={isFollowing}
          emptyMessage={emptyMessage}
          messages={messages}
          attachments={attachments}
          isFetchingPreviousMessages={isFetchingPreviousMessages}
          attachmentHandlers={{
            onAttachmentRemove: handleRemoveAttachment,
            onAttachmentSaved: handleAddAttachment,
            onAttachmentSaveFailed: handleAttachmentSaveFailed,
            onClearAttachments: handleClearAttachments
          }}
          handlers={{
            onLoadPreviousMessages,
            onMinimize: handleMinimize,
            onOpenParticipants: showParticipants,
            onCloseParticipants: handleCloseParticipants
          }}
        />
      </ChatBodyWrap>
    </div>
  );
};
